import {Component, DestroyRef, inject, Inject, OnInit} from '@angular/core';
import {MAT_DIALOG_DATA, MatDialogRef} from '@angular/material/dialog';
import {FormControl, FormGroup, Validators} from '@angular/forms';
import {ItemsFacadeService} from '@emm/management/services/items-facade.service';
import {ItemAttributeType} from '@emm/core/models/enums';
import {formatDateToMidnight} from '@emm/core/util/menu-date.util';
import {BaseItemRequestPayload} from '@emm/common-items/models/item.model';
import {ItemTypeService} from '@emm/core/services/item-type.service';
import {BehaviorSubject} from 'rxjs';
import {switchMap, tap} from 'rxjs/operators';
import {CommonItemAttribute} from '@emm/common-items/models/commonItemAttribute.model';
import {UserService} from '@emm/core/services/user.service';
import {NotificationService} from '@emm/shared/services/notification.service';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {formatFieldName} from '@emm/core/util/string-format.util';
import {ItemType} from '@emm/core/models/item-type.model';

@Component({
  selector: 'emm-create-common-item-dialog',
  templateUrl: './create-common-item-dialog.component.html',
  styleUrl: './create-common-item-dialog.component.scss'
})
export class CreateCommonItemDialogComponent implements OnInit {
    private readonly destroyRef: DestroyRef = inject(DestroyRef);

    effectiveDate: Date | null = new Date(Date.now() + 24 * 60 * 60 * 1000);
    classForm = new FormGroup ({
        class: new FormControl('ITEM'),
        effectiveDate: new FormControl(this.effectiveDate),
    });
    form: FormGroup;
    formReady = false;
    globalAttributes;
    classAttributes: CommonItemAttribute[] = [
        {
            attributeGroup: 'CORE',
            countryCode: 'US',
            language: 'en',
            name: 'Type',
            required: true,
            type: ItemAttributeType.Select,
            jsonProperty: 'type',
            tag: 'TYPE',
            value: null,
        }
    ];
    sortedAttributes;
    allTypes$ = new BehaviorSubject<ItemType[]>([]);
    user;

    constructor(
        private dialogRef: MatDialogRef<CreateCommonItemDialogComponent>,
        @Inject(MAT_DIALOG_DATA) private data: {},
        private itemsFacade: ItemsFacadeService,
        private typeService: ItemTypeService,
        private userService: UserService,
        private notificationService: NotificationService,
    ) {}

    ngOnInit() {
        this.userService.getUser().subscribe(user => {
            this.user = user;
            if (!this.user) {
                this.notificationService.error('Failed to load user')
            }
        });

        this.typeService.getAllItemTypes().pipe(
            tap(allTypes => {
                this.allTypes$.next(allTypes);
            }),
            switchMap(() => this.itemsFacade.attributes$),
            tap(globalAttributes => {
                globalAttributes.push({
                    attributeGroup: 'CORE',
                    dateUpdated: formatDateToMidnight(new Date().toISOString()),
                    defaultValue: undefined,
                    description: '',
                    itemTypeScope: [],
                    locked: false,
                    name: 'Tag',
                    options: undefined,
                    regionScope: undefined,
                    required: true,
                    scope: undefined,
                    type: ItemAttributeType.Text,
                    updatedBy: '',
                    validationExpression: '',
                    jsonProperty: 'tag',
                    tag: 'TAG'
                });
                this.globalAttributes = globalAttributes;
                // TODO: Add logic that only allows relevant attributes to be added to the form.
                //  This should be based on the class the user selects.
                this.buildForm();
            })
        ).subscribe();

        this.valueChangeHandler();
    }

    sortFormAttributes<T extends Record<string, any>>(attributes: T[]): T[] {
        const getName: (item: T) => string = (item) => item.name || item.jsonProperty || '';
        const isRequired: (item: T) => boolean = (item) => item.required;

        const sorted = attributes.sort((a, b) => {
            const aRequired = isRequired(a);
            const bRequired = isRequired(b);

            if (aRequired !== bRequired) {
                return aRequired ? -1 : 1;
            }

            const aName = getName(a).toLowerCase();
            const bName = getName(b).toLowerCase();
            return aName.localeCompare(bName);
        });
        const priorityOrder = ['tag', 'name'];
        const lower = (v: string) => v?.toLowerCase();
        const priority = priorityOrder.flatMap(p => sorted.filter(a => lower(getName(a)) === p || lower(a.jsonProperty) === p));
        if (priority.length) {
            const rest = sorted.filter(a => !priorityOrder.includes(lower(getName(a))) && !priorityOrder.includes(lower(a.jsonProperty)));
            return [...priority, ...rest];
        }
        return sorted;
    }

    buildForm() {
        const typeAttribute = this.classAttributes.find(attribute => attribute.tag === 'TYPE');
        const selectedClass = this.classForm.controls['class'].value;

        if (typeAttribute) {
            const allTypes = this.allTypes$.value;
            if (!allTypes || allTypes.length === 0) {
                typeAttribute.options = [];
                console.log('buildForm - no types loaded yet');
            } else if (selectedClass) {
                typeAttribute.options = allTypes.filter(type => type.classTag === selectedClass);
            } else {
                typeAttribute.options = allTypes;
            }
        } else {
            console.log('buildForm - typeAttribute not found!');
        }

        this.sortedAttributes = this.sortFormAttributes(this.globalAttributes);
        this.sortedAttributes = this.sortedAttributes.map(attr => ({
            ...attr,
            name: formatFieldName(attr.name)
        }));
        this.classAttributes = this.classAttributes.map(attr => ({
            ...attr,
            name: formatFieldName(attr.name)
        }));

        const dynamicGroup = {};
        this.classAttributes.forEach(attribute => {
            dynamicGroup[attribute.jsonProperty] = new FormControl({value: null, disabled: false}, Validators.required);
        });
        // Only add required global attributes to the form
        for (const attribute of this.sortedAttributes) {
            if (attribute.required) {
                dynamicGroup[attribute.jsonProperty] = new FormControl({value: null, disabled: false}, Validators.required);
            }
        }
        this.form = new FormGroup(dynamicGroup);
        this.formReady = true;
    }

    close(): void {
        this.dialogRef.close();
    }

    save() {
        const formValues = this.form.value;
        let attributes: CommonItemAttribute[] = Object.entries(this.form.controls)
            .map(([jsonProperty, control]) => {
                const attr = this.sortedAttributes.find(a => a.jsonProperty === jsonProperty)
                    ?? this.classAttributes.find(a => a.jsonProperty === jsonProperty);

                return {
                    tag: attr?.tag,
                    jsonProperty,
                    value: (control.value === 'True' || control.value === 'False')
                        ? this.convertStringToBoolean(control.value) : control.value,
                    attributeGroup: attr?.attributeGroup,
                    type: attr?.type,
                    name: attr?.name,
                    countryCode: 'US',
                    language: 'en'
                } as CommonItemAttribute;
            });
        attributes = this.buildAttributesForCreate(this.form, formValues, attributes);
        const newItem: BaseItemRequestPayload = {
            tag: attributes.find(attribute => attribute.jsonProperty === 'tag')?.value as string,
            type: attributes.find(attribute => attribute.jsonProperty === 'type')?.value as string,
            isLocked: false,
            rmi: attributes.find(attribute => attribute.jsonProperty === 'retailModifiedItemId')?.value as number,
            name: attributes.find(attribute => attribute.jsonProperty === 'name')?.value as string,
            image: attributes.find(attribute => attribute.jsonProperty === 'webImage')?.value as string,
            description: attributes.find(attribute => attribute.jsonProperty === 'description')?.value as string,
            country: 'US',
            locationSetTag: 'CHAIN',
            attributes,
        };
        // const effectiveDate = formatDateToMidnight(this.effectiveDate.toISOString());
        const requestedBy = `${this.user?.firstName} ${this.user?.lastName}` ?? this.user?.login ?? 'unknown';
        const itemClass = this.classForm.controls['class'].value;
        this.dialogRef.close({newItem, requestedBy, class: itemClass});
    }

    onDateChange(event: any) {
        this.effectiveDate = event?.value ?? null;
    }

    convertStringToBoolean(value: string): boolean {
        return value === 'True';
    }

    buildAttributesForCreate(
        form,
        formValues = {},
        attributes = [],
    ) {
        const hasFormGet = form && typeof form.get === 'function';

        const isDirty = (property) => {
            if (!property) {
                return false;
            }
            if (hasFormGet) {
                const ctrl = form.get(property);
                return !!(ctrl && ctrl.dirty);
            }
            return false;
        };

        return attributes
            .filter(attr => attr.jsonProperty !== 'components')
            .filter(attr => isDirty(attr.jsonProperty))
            .map(attr => {
                const resolved = formValues?.[attr.jsonProperty] !== undefined
                    ? formValues[attr.jsonProperty]
                    : attr.value;

                const value = Array.isArray(resolved) ? JSON.stringify(resolved) : resolved;

                return {
                    ...attr,
                    value
                };
            });
    }

    hasAttribute(tag: string): boolean {
        return this.classAttributes.some(a => a.tag === tag);
    }

    getRequiredGlobalAttributes() {
        return this.sortedAttributes?.filter(attr => attr.required) ?? [];
    }

    buildModifierGroupAttributes() {
        const modifierGroupAttributes: CommonItemAttribute[] = [
            {
                tag: 'MODIFIER_TYPE_GROUPING',
                value: undefined,
                jsonProperty: 'modifierTypeGrouping',
                attributeGroup: 'CORE',
                type: 'SELECT',
                name: 'Modifier Type Grouping',
                countryCode: 'US',
                language: 'en',
                options: [
                    {
                        label: 'Bread',
                        value: 'Bread'
                    },
                    {
                        label: 'Dressings & Condiments',
                        value: 'Dressings & Condiments'
                    },
                    {
                        label: 'Extras',
                        value: 'Extras'
                    },
                    {
                        label: 'Ice Amount',
                        value: 'Ice Amount'
                    },
                    {
                        label: 'Toppings',
                        value: 'Toppings'
                    },
                    {
                        label: 'Sauces & Condiments',
                        value: 'Sauces & Condiments'
                    },
                    {
                        label: 'Salt',
                        value: 'Salt'
                    },
                    {
                        label: 'Condiments',
                        value: 'Condiments'
                    },
                    {
                        label: 'Protein',
                        value: 'Protein'
                    },
                    {
                        label: 'Cheese',
                        value: 'Cheese'
                    },
                    {
                        label: 'Sauces',
                        value: 'Sauces'
                    },
                    {
                        label: 'Dough',
                        value: 'Dough'
                    },
                    {
                        label: 'Veggies',
                        value: 'Veggies'
                    },
                    {
                        label: 'Sauces & Seasonings',
                        value: 'Sauces & Seasonings'
                    },
                    {
                        label: 'Base',
                        value: 'Base'
                    },
                    {
                        label: 'Dipping Sauces',
                        value: 'Dipping Sauces'
                    },
                    {
                        label: 'Seasonings',
                        value: 'Seasonings'
                    },
                    {
                        label: 'Dressings',
                        value: 'Dressings'
                    }
                ],
                required: true,
            },
            {
                tag: 'MAXIMUM',
                value: undefined,
                jsonProperty: 'maximum',
                attributeGroup: 'CORE',
                type: 'NUM',
                name: 'Maximum',
                countryCode: 'US',
                language: 'en',
                required: true,
            },
            {
                tag: 'MINIMUM',
                value: undefined,
                jsonProperty: 'minimum',
                attributeGroup: 'CORE',
                type: 'NUM',
                name: 'Minimum',
                countryCode: 'US',
                language: 'en',
                required: false,
            },
        ];
        return modifierGroupAttributes;
    }
    buildModifierAttributes() {
        const modifierAttributes: CommonItemAttribute[] = [
            {
                tag: 'MODIFIER_TYPE',
                value: undefined,
                jsonProperty: 'modifierType',
                attributeGroup: 'CORE',
                type: 'SELECT',
                name: 'Modifier Type',
                countryCode: 'US',
                language: 'en',
                options: [
                    {
                        label: 'RECIPE',
                        value: 'RECIPE'
                    },
                    {
                        label: 'DEFAULT',
                        value: 'DEFAULT'
                    },
                    {
                        label: 'EXTRA',
                        value: 'EXTRA'
                    },
                    {
                        label: 'NO_REFUND_RECIPE',
                        value: 'NO_REFUND_RECIPE'
                    }
                ],
                required: true,
            },
            {
                tag: 'MAXIMUM',
                value: undefined,
                jsonProperty: 'maximum',
                attributeGroup: 'CORE',
                type: 'NUM',
                name: 'Maximum',
                countryCode: 'US',
                language: 'en',
                required: false
            },
        ];
        return modifierAttributes;
    }

    valueChangeHandler() {
        this.classForm.controls['class'].valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((value) => {
            const modifierGroupAttributes = this.buildModifierGroupAttributes();
            const modifierAttributes = this.buildModifierAttributes();
            if (value === 'MODIFIER_GROUPING') {
                const tagsToRemove = new Set<string>([
                    ...modifierAttributes.map(a => a.tag)
                ]);

                const beforeLength = this.classAttributes.length;
                this.classAttributes = this.classAttributes.filter(attr => !tagsToRemove.has(attr.tag));
                const afterLength = this.classAttributes.length;

                let changed = false;
                modifierGroupAttributes.forEach(attribute => {
                    if (!this.hasAttribute(attribute.tag)) {
                        this.classAttributes.push(attribute);
                        changed = true;
                    }
                });
                if (changed || afterLength !== beforeLength) {
                    this.buildForm();
                }
            } else if (value === 'MODIFIER') {
                const tagsToRemove = new Set<string>([
                    ...modifierGroupAttributes.map(a => a.tag)
                ]);

                const beforeLength = this.classAttributes.length;
                this.classAttributes = this.classAttributes.filter(attr => !tagsToRemove.has(attr.tag));
                const afterLength = this.classAttributes.length;

                let changed = false;
                modifierAttributes.forEach(attribute => {
                    if (!this.hasAttribute(attribute.tag)) {
                        this.classAttributes.push(attribute);
                        changed = true;
                    }
                });
                if (changed || afterLength !== beforeLength) {
                    console.log(this.classAttributes);
                    this.buildForm();
                }
            } else {
                const tagsToRemove = new Set<string>([
                    ...modifierGroupAttributes.map(a => a.tag),
                    ...modifierAttributes.map(a => a.tag)
                ]);

                const beforeLength = this.classAttributes.length;
                this.classAttributes = this.classAttributes.filter(attr => !tagsToRemove.has(attr.tag));
                const afterLength = this.classAttributes.length;

                if (afterLength !== beforeLength) {
                    this.buildForm();
                }
            }
        });
    }
}
